<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { OnClickOutside } from '@vueuse/components';
import CrmPipelinesAPI from 'dashboard/api/crm/pipelines';

const props = defineProps({
  header: {
    type: String,
    default: '',
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  pollingEnabled: {
    type: Boolean,
    default: false,
  },
  pollingRemaining: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['toggle-select-mode', 'toggle-polling']);

const pollingTimeFormatted = computed(() => {
  const m = Math.floor(props.pollingRemaining / 60);
  const s = props.pollingRemaining % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
});

const selectedStatus = defineModel('status', { default: 'open' });
const selectedInboxId = defineModel('inboxId', { default: null });
const onlyWaiting = defineModel('onlyWaiting', { default: false });
const selectedPipelineId = defineModel('pipelineId', { default: null });
const selectedStageId = defineModel('stageId', { default: null });

const inboxes = useMapGetter('inboxes/getInboxes');
const agentStatus = useMapGetter('agents/getAgentStatus');

const statusOptions = [
  { key: 'open', label: 'Abertos' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'all', label: 'Todos' },
];

// Inbox dropdown state
const showInboxDropdown = ref(false);
const inboxSearch = ref('');

const filteredInboxes = computed(() => {
  if (!inboxSearch.value) return inboxes.value || [];
  const q = inboxSearch.value.toLowerCase();
  return (inboxes.value || []).filter(i => i.name.toLowerCase().includes(q));
});

const selectedInboxName = computed(() => {
  if (!selectedInboxId.value) return null;
  const inbox = (inboxes.value || []).find(i => i.id === selectedInboxId.value);
  return inbox?.name || null;
});

const selectInbox = id => {
  selectedInboxId.value = id;
  showInboxDropdown.value = false;
  inboxSearch.value = '';
};

const clearInbox = () => {
  selectedInboxId.value = null;
  showInboxDropdown.value = false;
  inboxSearch.value = '';
};

const toggleInboxDropdown = () => {
  showInboxDropdown.value = !showInboxDropdown.value;
  if (showInboxDropdown.value) {
    inboxSearch.value = '';
  }
};

// Pipeline / Stage filter
const pipelines = ref([]);
const showPipelineDropdown = ref(false);

onMounted(async () => {
  try {
    const { data } = await CrmPipelinesAPI.get();
    pipelines.value = data || [];
  } catch (e) {
    // silent
  }
});

const selectedPipeline = computed(() => {
  if (!selectedPipelineId.value) return null;
  return pipelines.value.find(p => p.id === selectedPipelineId.value) || null;
});

const stages = computed(() => {
  if (!selectedPipeline.value) return [];
  return selectedPipeline.value.stages || [];
});

const selectedStageName = computed(() => {
  if (!selectedStageId.value) return null;
  const stage = stages.value.find(s => s.id === selectedStageId.value);
  return stage?.name || null;
});

// Reset stage when pipeline changes
watch(selectedPipelineId, () => {
  selectedStageId.value = null;
});

const selectPipeline = id => {
  selectedPipelineId.value = id;
  selectedStageId.value = null;
  if (!id) showPipelineDropdown.value = false;
};

const selectStage = id => {
  selectedStageId.value = id;
  showPipelineDropdown.value = false;
};

const clearPipeline = () => {
  selectedPipelineId.value = null;
  selectedStageId.value = null;
  showPipelineDropdown.value = false;
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 lg:px-6 py-2.5 border-b border-n-weak sticky top-0 bg-n-surface-1 z-10">
    <div class="flex items-center gap-2 min-w-0 shrink-0">
      <span class="i-lucide-layout-dashboard size-5 text-n-slate-12 hidden sm:block" />
      <h1 class="text-sm lg:text-base font-semibold text-n-slate-12 whitespace-nowrap">
        {{ header || 'Monitor de Atendimentos' }}
      </h1>
      <button
        class="flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        :class="pollingEnabled
          ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
          : 'bg-n-alpha-1 text-n-slate-9 hover:bg-n-alpha-2'" @click="emit('toggle-polling')">
        <span class="size-1.5 rounded-full" :class="pollingEnabled ? 'bg-green-500 animate-pulse' : 'bg-n-slate-7'" />
        <template v-if="pollingEnabled">
          Ao vivo · {{ pollingTimeFormatted }}
        </template>
        <template v-else>
          Clique para atualizar em tempo real
        </template>
      </button>
    </div>

    <div class="flex items-center gap-1.5 lg:gap-2 flex-wrap ml-auto">
      <div class="flex items-center bg-n-alpha-1 rounded-lg p-0.5">
        <button v-for="opt in statusOptions" :key="opt.key"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors" :class="selectedStatus === opt.key
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            " @click="selectedStatus = opt.key">
          {{ opt.label }}
        </button>
      </div>

      <button class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors border"
        :class="onlyWaiting
            ? 'bg-ruby-800/10 text-ruby-800 dark:text-ruby-400 border-ruby-800/20'
            : 'text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2 border-transparent'
          " @click="onlyWaiting = !onlyWaiting">
        <span class="i-lucide-clock size-3.5" />
        Sem resposta
      </button>

      <OnClickOutside v-if="pipelines.length" @trigger="showPipelineDropdown = false">
        <div class="relative">
          <button v-if="!selectedPipeline"
            class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-n-slate-11 bg-n-alpha-1 rounded-lg hover:bg-n-alpha-2 transition-colors border border-transparent"
            @click="showPipelineDropdown = !showPipelineDropdown">
            <span class="i-lucide-git-branch size-3.5" />
            Funil
            <span class="i-lucide-chevron-down size-3 text-n-slate-9" />
          </button>
          <button v-else
            class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/30 rounded-lg border border-violet-200 dark:border-violet-800/40"
            @click="showPipelineDropdown = !showPipelineDropdown">
            <span class="i-lucide-git-branch size-3.5" />
            {{ selectedPipeline.name }}
            <template v-if="selectedStageName">
              <span class="text-violet-400 mx-0.5">→</span>
              <span class="font-bold">{{ selectedStageName }}</span>
            </template>
            <span class="i-lucide-x size-3 text-violet-500 hover:text-violet-700 cursor-pointer"
              @click.stop="clearPipeline" />
          </button>

          <div v-if="showPipelineDropdown"
            class="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg shadow-lg z-50 overflow-hidden">
            <div v-if="!selectedPipelineId" class="max-h-48 overflow-y-auto py-1">
              <p class="px-3 py-1 text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider">
                Selecionar funil
              </p>
              <button v-for="pipeline in pipelines" :key="pipeline.id"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors text-n-slate-11"
                @click="selectPipeline(pipeline.id)">
                <span class="i-lucide-git-branch size-3.5 text-n-slate-9" />
                {{ pipeline.name }}
                <span class="ml-auto text-[10px] text-n-slate-8">
                  {{ (pipeline.stages || []).length }} etapas
                </span>
              </button>
            </div>

            <div v-else class="max-h-48 overflow-y-auto py-1">
              <button
                class="w-full flex items-center gap-2 px-3 py-1 text-[10px] text-left text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
                @click="selectPipeline(null)">
                <span class="i-lucide-chevron-left size-3" />
                Voltar
              </button>
              <p class="px-3 py-1 text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider">
                {{ selectedPipeline.name }} — Etapas
              </p>
              <button
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors"
                :class="!selectedStageId
                    ? 'text-violet-700 font-semibold'
                    : 'text-n-slate-11'
                  " @click="selectStage(null)">
                Todas as etapas
                <span v-if="!selectedStageId" class="i-lucide-check size-3.5 ml-auto text-violet-700" />
              </button>
              <button v-for="stage in stages" :key="stage.id"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors"
                :class="selectedStageId === stage.id
                    ? 'text-violet-700 font-semibold'
                    : 'text-n-slate-11'
                  " @click="selectStage(stage.id)">
                {{ stage.name }}
                <span v-if="selectedStageId === stage.id" class="i-lucide-check size-3.5 ml-auto text-violet-700" />
              </button>
            </div>
          </div>
        </div>
      </OnClickOutside>

      <OnClickOutside @trigger="showInboxDropdown = false">
        <div class="relative">
          <button v-if="!selectedInboxName"
            class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-n-slate-11 bg-n-alpha-1 rounded-lg hover:bg-n-alpha-2 transition-colors border border-transparent"
            @click="toggleInboxDropdown">
            <span class="i-lucide-inbox size-3.5" />
            Caixa de entrada
            <span class="i-lucide-chevron-down size-3 text-n-slate-9" />
          </button>
          <button v-else
            class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-n-brand bg-n-brand/10 rounded-lg border border-n-brand/20"
            @click="toggleInboxDropdown">
            <span class="i-lucide-inbox size-3.5" />
            {{ selectedInboxName }}
            <span class="i-lucide-x size-3 text-n-brand/60 hover:text-n-brand cursor-pointer"
              @click.stop="clearInbox" />
          </button>

          <div v-if="showInboxDropdown"
            class="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg shadow-lg z-50 overflow-hidden">
            <div class="p-2 border-b border-n-weak">
              <input v-model="inboxSearch" type="text" placeholder="Buscar caixa..."
                class="w-full px-2.5 py-1.5 text-xs bg-n-alpha-1 rounded-md outline-none text-n-slate-12 placeholder:text-n-slate-8 focus:ring-1 focus:ring-n-brand" />
            </div>
            <div class="max-h-48 overflow-y-auto py-1">
              <button
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors"
                :class="!selectedInboxId
                    ? 'text-n-brand font-semibold'
                    : 'text-n-slate-11'
                  " @click="clearInbox">
                <span class="i-lucide-layers size-3.5 text-n-slate-9" />
                Todas as caixas
                <span v-if="!selectedInboxId" class="i-lucide-check size-3.5 ml-auto text-n-brand" />
              </button>
              <button v-for="inbox in filteredInboxes" :key="inbox.id"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors"
                :class="selectedInboxId === inbox.id
                    ? 'text-n-brand font-semibold'
                    : 'text-n-slate-11'
                  " @click="selectInbox(inbox.id)">
                <span class="i-lucide-inbox size-3.5 text-n-slate-9" />
                {{ inbox.name }}
                <span v-if="selectedInboxId === inbox.id" class="i-lucide-check size-3.5 ml-auto text-n-brand" />
              </button>
              <div v-if="!filteredInboxes.length" class="px-3 py-3 text-xs text-n-slate-9 text-center">
                Nenhuma caixa encontrada
              </div>
            </div>
          </div>
        </div>
      </OnClickOutside>

      <button class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors border"
        :class="selectMode
          ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
          : 'text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2 border-transparent'" @click="emit('toggle-select-mode')">
        <span class="i-lucide-check-square size-3.5" />
        {{ selectMode ? 'Selecionando...' : 'Selecionar' }}
      </button>

      <div class="w-px h-5 bg-n-weak" />
      <div class="flex items-center gap-2.5 text-xs text-n-slate-10">
        <span class="flex items-center gap-1">
          <span class="size-2 rounded-full bg-green-500" />
          {{ agentStatus?.online || 0 }}
        </span>
        <span class="flex items-center gap-1">
          <span class="size-2 rounded-full bg-yellow-500" />
          {{ agentStatus?.busy || 0 }}
        </span>
        <span class="flex items-center gap-1">
          <span class="size-2 rounded-full bg-n-slate-7" />
          {{ agentStatus?.offline || 0 }}
        </span>
      </div>
    </div>
  </div>
</template>
